import type { Provenance, SearchedDoc, SourceAnchor } from '@/services/ingestion';

/**
 * What the one-datapoint card says, and how it names where a value came from.
 * Pure data and pure functions, kept apart from the component so the card file
 * exports a component and nothing else.
 */

// The controlled vocabularies for datapoints the record stores as enums (property type, purpose, index, rate
// type, frequency, lien) are the deal catalogue's own — see `useDatapointOptions`. None is typed here.

export const STATUS_META: Record<string, { label: string; cls: string; dot: string }> = {
  conflict: { label: 'conflict', cls: 'text-red-300 bg-red-400/10 border-red-400/25', dot: 'bg-red-400' },
  flagged: { label: 'confirm', cls: 'text-amber-300 bg-amber-400/10 border-amber-400/25', dot: 'bg-amber-400' },
  missing: { label: 'missing', cls: 'text-stone-300 bg-stone-500/10 border-stone-500/30', dot: 'bg-stone-500' },
  derivable: { label: 'derivable', cls: 'text-sky-300 bg-sky-400/10 border-sky-400/25', dot: 'bg-sky-400' },
  found: { label: 'found', cls: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/20', dot: 'bg-emerald-400' },
  resolved: { label: 'decided', cls: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/20', dot: 'bg-emerald-400' },
};

export const OUTCOME_LABEL: Record<SearchedDoc['outcome'], string> = {
  found: 'states it',
  no_label_match: 'nothing found',
  label_seen_unparsed: 'labeled, unreadable value',
  unread_model: 'not read — needs onboarding',
  unreadable: 'could not be read',
  not_searched: 'no readable text',
};

export const OUTCOME_CLS: Record<SearchedDoc['outcome'], string> = {
  found: 'text-emerald-300',
  no_label_match: 'text-stone-500',
  label_seen_unparsed: 'text-amber-300',
  unread_model: 'text-amber-300',
  unreadable: 'text-red-300',
  not_searched: 'text-stone-600',
};

export function displayValue(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') {
    const u = v as { count?: number; kind?: string };
    if (u.count != null) return `${u.count.toLocaleString()} ${u.kind ?? ''}`.trim();
    return JSON.stringify(v);
  }
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
}

/** "page 4 · line 62" / "Sheet Sizing · D14" — where inside the document. */
export function locatorText(p?: Provenance | null): string | null {
  const loc = p?.locator;
  if (!loc) return null;
  const parts: string[] = [];
  if (loc.sheet) parts.push(`sheet ${loc.sheet}`);
  if (loc.cell) parts.push(`cell ${loc.cell}`);
  if (loc.page) parts.push(`page ${loc.page}`);
  if (loc.lineNo) parts.push(`line ${loc.lineNo}`);
  if (loc.region === 'quoted') parts.push('quoted text');
  if (loc.ocr) parts.push('OCR reading');
  return parts.length ? parts.join(' · ') : null;
}

/** The anchor the source viewer needs to scroll to and highlight this value. */
export function anchorFor(p?: Provenance | null): SourceAnchor | null {
  if (!p) return null;
  const loc = p.locator ?? {};
  return {
    kind: (loc.kind ?? undefined) as SourceAnchor['kind'],
    lineNo: loc.lineNo ?? undefined,
    page: loc.page ?? undefined,
    cell: loc.sheet && loc.cell ? `${loc.sheet}!${loc.cell}` : (loc.cell ?? undefined),
    snippet: p.snippet ?? undefined,
    sha256: p.doc?.sha256 ?? undefined,
    region: (loc.region ?? undefined) as SourceAnchor['region'],
  };
}

export const DECISION_TEXT: Record<string, string> = {
  confirm: 'Confirmed as read',
  correct: 'Corrected',
  accept_located: 'Taken from the source you pointed at',
  unavailable: 'Recorded as unavailable',
};
